/**
 * Build the DrugDetailPage viewer URL for a CEPI "Sources" citation that
 * points at a regulatory document (SBA / EPAR / UK PIL / CA PM / AU).
 *
 * The regulatory viewers are only reachable through the parent drug's page,
 * so the citation MUST carry both ``drug_id`` (the owning drug) and
 * ``document_id`` (the regulatory document). The URL shapes mirror exactly
 * what ``DrugDetailPage`` already parses to mount + highlight the viewer:
 *
 *   sba    → /drug/:drug_id?source=sba&version=:document_id&sbaChunkIds=:chunk_id
 *   epar   → /drug/:drug_id?source=epar&version=:document_id&eparChunkIds=:chunk_id
 *   uk_pil → /drug/:drug_id?source=ukpil&version=:document_id&ukPilChunkIds=:chunk_id
 *   ca_pm  → /drug/:drug_id?source=capm&version=:document_id&caPmChunkIds=:chunk_id
 *   au     → /drug/:drug_id?source=audoc&version=:document_id&auChunkIds=:chunk_id
 *   eu     → /drug/:drug_id?source=eudoc&version=:document_id&euChunkIds=:chunk_id
 *   jp_pdf → /drug/:drug_id?source=jppdfdoc&version=:document_id&jpPdfChunkIds=:chunk_id
 *   zh_pdf → /drug/:drug_id?source=zhpdfdoc&version=:document_id&zhPdfChunkIds=:chunk_id
 *   approval_letter → /drug/:drug_id?source=approval-letter&version=:document_id&approvalLetterChunkIds=:chunk_id&approvalLetterPage=:page
 *
 * Returns ``null`` when the citation is not a routable regulatory citation
 * (non-regulatory ``document_type``, or missing ``drug_id`` / ``document_id``);
 * callers treat ``null`` as "no navigation" and no-op.
 */
import { ANCHOR_TEXT_MAX } from '../components/viewer/anchorBbox';

export interface RegulatoryCitation {
  document_type?: string | null;
  /** CEPI reports use `corpus`; Q&A citations use `document_type`. */
  corpus?: string | null;
  drug_id?: number | string | null;
  document_id?: number | string | null;
  chunk_id?: number | null;
  /** One entry per cited span. A citation whose evidence crosses a page break
   *  owns more than one chunk, and the viewer only draws a box for a chunk it
   *  FETCHED — so every owner has to reach the URL, not just the first. */
  evidence_anchors?: ReadonlyArray<{
    chunk_id?: number | null;
    start?: string | null;
    end?: string | null;
    /** The whole cited sentence, scored by the matcher's fifth pass. */
    text?: string | null;
  }> | null;
  /** The whole cited sentence for the single-anchor fallback below. */
  evidence_text?: string | null;
  /** The FIRST cited span's sentence, kept for consumers that predate
   *  `evidence_anchors`. It is also the only anchor a citation has when it
   *  reaches the resolver fallback instead of the captured-span path, which is
   *  why the URL writer has to be able to fall back to it — see
   *  `citeAnchorTriples`. */
  anchor_start?: string | null;
  anchor_end?: string | null;
  page?: number | null;
  citation_alignment?: 'source_aligned' | 'chunk_only' | null;
}

/**
 * The cited sentences as positional `[chunkId, start, end]` triples for the URL.
 *
 * THE TWO HALVES OF THE URL MUST READ THE SAME SOURCES. The chunk param comes
 * from `citedChunkIds`, which falls back to `cit.chunk_id`; this read only
 * `evidence_anchors` and had no fallback. A citation carrying `chunk_id` +
 * `anchor_start` and no per-span anchors therefore emitted the chunk id and
 * omitted the sentence, so the destination fetched the right chunk, scrolled to
 * the right page, and drew nothing — silently, which is the failure mode this
 * whole workstream keeps rediscovering.
 *
 * That is not a rare shape: it is every citation that reaches the resolver
 * fallback rather than the captured-span path. Measured on staging 2026-10-09
 * (INFLECTRA doc 1978 §13 page 16): batch payload `{chunk_ids: [418489]}`, no
 * box, while the sibling chunk 418490 — which did carry `evidence_anchors` —
 * highlighted.
 *
 * Per-span wins when present: `anchor_start` names only the FIRST span, so
 * preferring it would drop every later one.
 */
export function citeAnchorTriples(
  cit: RegulatoryCitation,
): (string | number | null | undefined)[][] {
  // The FOURTH element is the whole cited sentence, and it is what the
  // matcher's fifth pass scores. Without it a citation clicked into a NEW TAB
  // silently loses the recall that pass exists for: the originating tab holds
  // the sentence in React state, the new tab holds only this URL.
  //
  // Appended last, and OMITTED when empty. Appending keeps a URL written by an
  // older build parseable (the reader destructures four and tolerates three);
  // omitting keeps the common case byte-identical to what shipped, which
  // matters because several cited spans ride one URL and a sentence is two
  // orders of magnitude longer than a chunk id.
  // The cap is applied HERE as well as at every other producer of this needle.
  // It used to be omitted on this path only, so the same citation highlighted
  // differently depending on whether it opened in this tab (clamped by
  // `seedChunkAnchor`) or a new one (unclamped, straight off `evidence_text`,
  // which `types/qa.ts` defines as the cited sentences "in full, joined" and so
  // can be a paragraph). One constant, every writer.
  const tuple = (
    id: number, start: string, end: string, text: string | null | undefined,
  ) => (text ? [id, start, end, text.slice(0, ANCHOR_TEXT_MAX)] : [id, start, end]);

  const perSpan = (cit.evidence_anchors ?? [])
    .filter((a) => a.chunk_id != null && a.start)
    .map((a) => tuple(a.chunk_id!, a.start!, a.end ?? '', a.text));
  if (perSpan.length > 0) return perSpan;
  // `end` as '' exactly as the per-span branch writes it, so the reader parses
  // one shape rather than two.
  return cit.chunk_id != null && cit.anchor_start
    ? [tuple(cit.chunk_id, cit.anchor_start, cit.anchor_end ?? '', cit.evidence_text)]
    : [];
}

/**
 * Every chunk this citation's evidence lives in, in cited order, deduped.
 *
 * `chunk_id` leads: it was realigned upstream to span[0]'s owner and the viewer
 * scrolls to the FIRST id, so reordering would land the reader on a later span's
 * page. Remaining span owners follow — two sentences from one chunk are one
 * fetch.
 *
 * Exported because the viewer only draws a box for a chunk it FETCHED, and that
 * fetch is driven from two places: this module's URL (a tab that did not exist
 * at click time) and `DrugDetailPage`'s in-page click, which used to pass
 * `[citation.chunk_id]` and so could never show a second chunk's evidence. One
 * implementation, or the two drift and the bug comes back on one path only.
 */
export function citedChunkIds(cit: RegulatoryCitation): number[] {
  const ids: number[] = [];
  for (const id of [cit.chunk_id, ...(cit.evidence_anchors ?? []).map((a) => a.chunk_id)]) {
    if (id != null && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

const REGULATORY_ROUTE: Record<string, { source: string; chunkParam: string }> = {
  sba: { source: 'sba', chunkParam: 'sbaChunkIds' },
  epar: { source: 'epar', chunkParam: 'eparChunkIds' },
  uk_pil: { source: 'ukpil', chunkParam: 'ukPilChunkIds' },
  ca_pm: { source: 'capm', chunkParam: 'caPmChunkIds' },
  au: { source: 'audoc', chunkParam: 'auChunkIds' },
  eu: { source: 'eudoc', chunkParam: 'euChunkIds' },
  jp_pdf: { source: 'jppdfdoc', chunkParam: 'jpPdfChunkIds' },
  zh_pdf: { source: 'zhpdfdoc', chunkParam: 'zhPdfChunkIds' },
  ch_pdf: { source: 'chpdfdoc', chunkParam: 'chPdfChunkIds' },
  de_pdf: { source: 'depdfdoc', chunkParam: 'dePdfChunkIds' },
  // IE / IT open in the same national label-PDF viewer slot as DE
  ie_pdf: { source: 'depdfdoc', chunkParam: 'dePdfChunkIds' },
  it_pdf: { source: 'depdfdoc', chunkParam: 'dePdfChunkIds' },
  // FR ANSM HTML labels: the viewer opens the document (``frhtmldoc``); it has
  // no chunk-highlight param yet, so the chunk id is carried but unread.
  fr_html: { source: 'frhtmldoc', chunkParam: 'frHtmlChunkIds' },
  fda_label_pdf: { source: 'fdalabelpdfdoc', chunkParam: 'fdaLabelPdfChunkIds' },
  approval_letter: {
    source: 'approval-letter',
    chunkParam: 'approvalLetterChunkIds',
  },
};

function positiveInteger(value: number | string | null | undefined): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

/** A Cogni result row -> the document its best evidence came from (the
 *  backend's ``evidence.source_family`` + ``document_id``). ``null`` when the
 *  evidence names no family document (US monolithic label, a response cached
 *  before these fields, or a family without a document viewer): the caller
 *  keeps the drug-level link. */
export function cogniEvidenceHref(
  drugId: number,
  ev: { source_family?: string | null; document_id?: number | null; chunk_id?: number | null } | null | undefined,
): string | null {
  if (!ev?.source_family || !ev.document_id) return null;
  return regulatoryCitationHref({
    document_type: ev.source_family, drug_id: drugId, document_id: ev.document_id, chunk_id: ev.chunk_id ?? null,
  });
}

export function regulatoryCitationHref(cit: RegulatoryCitation): string | null {
  // Native CEPI evidence uses the corpus name; its FDA approval-letter corpus
  // is rendered by the approval-letter viewer family. FDA correspondence
  // (`fda_letter`) deliberately has no route here.
  const routeKey = cit.document_type ?? (
    cit.corpus === 'fda_approval_letter' ? 'approval_letter' : cit.corpus
  );
  const route = routeKey ? REGULATORY_ROUTE[routeKey] : undefined;
  if (!route) return null;
  const drugId = positiveInteger(cit.drug_id);
  const documentId = positiveInteger(cit.document_id);
  if (drugId === null || documentId === null) return null;

  let href = `/drug/${drugId}?source=${route.source}&version=${documentId}`;
  const chunkOnly = cit.citation_alignment === 'chunk_only';
  if (!chunkOnly) {
    const ids = citedChunkIds(cit);
    if (ids.length > 0) {
      href += `&${route.chunkParam}=${ids.join(',')}`;
    }
    // The cited SENTENCES, so a tab that did not exist when the citation was
    // clicked can still draw the box. ``seedChunkAnchor`` writes these into the
    // originating page's React state; a new tab mounts with none, which is why
    // it scrolls to the right page and highlights nothing.
    //
    // Positional triples rather than objects: this rides a URL, and
    // ``[[7,"start","end"]]`` is far shorter than the keyed form for the same
    // information.
    const anchors = citeAnchorTriples(cit);
    if (anchors.length > 0) {
      href += `&citeAnchors=${encodeURIComponent(JSON.stringify(anchors))}`;
    }
  }
  if (
    routeKey === 'approval_letter'
    && Number.isInteger(cit.page)
    && (cit.page as number) >= 1
  ) {
    href += `&approvalLetterPage=${cit.page}`;
  } else if (
    chunkOnly
    && Number.isInteger(cit.page)
    && (cit.page as number) >= 1
  ) {
    href += `&citationPage=${cit.page}`;
  }
  return href;
}
